import React, { useMemo, useEffect, useCallback, useState } from "react";
import {
  Modal,
  Button,
  Popover,
  Form,
  Input,
  Row,
  Col,
  Select,
  InputNumber,
  AutoComplete,
} from "antd";
import { reportTypesService } from "../../../services/reportTypes";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../utils/notifications";
import { useDebounce, useFetch } from "../../../utils/hooks";

import styles from "../../styles/Registers.module.css";
import { showApiErrors } from "../../../utils/showApiErrors";
import { getSelectSpecializationOptions } from "../../../utils/options";

const { Option } = Select;

export const AddOrEditReportType = ({
  visibleModal,
  onCloseModal,
  onEdit,
  onAddReportType,
  reportType,
}) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [reportTypes, setReportTypes] = useState([]);
  const [specializations, setSpecializations] = useState([]);

  const initialValues = useMemo(() => {
    if (!reportType) {
      return undefined;
    }
    const { name, price, specialization } = reportType;

		let specializationId = specialization?.id

		if (!specializationId && typeof specialization === "number") {
			specializationId = specialization
		}

    return {
      name,
      price,
      specialization: specializationId,
    };
  }, [reportType]);

  useEffect(() => {
    async function getSpecializations() {
      setLoading(true);
      try {
        const { results } = await reportTypesService.getSpecializations();
        setSpecializations(results);
      } catch (e) {
        showApiErrors(e);
        console.log("ERROR get spec");
      } finally {
        setLoading(false);
      }
    }
    getSpecializations();
  }, []);

  const resetFields = useCallback(() => {
    form.resetFields();
  }, [form]);

  useEffect(() => {
    if (visibleModal) {
      resetFields();
    }
  }, [resetFields, visibleModal]);

  const onSubmit = async (values) => {
    setLoading(true);
    if (reportType) {
      try {
        await reportTypesService.editReportType(reportType.id, values);
        onCloseModal();
        openSuccessNotification({ message: "Izmjena uspješno sačuvana!" });
        await onEdit();
      } catch (e) {
        showApiErrors(e);
        console.log("ERROR edit");
      } finally {
        setLoading(false);
      }
    } else {
      try {
        await reportTypesService.addReportType(values);
        onCloseModal();
        openSuccessNotification({
          message: `Pregled uspješno kreiran!`,
        });
        await onAddReportType();
      } catch (e) {
        showApiErrors(e);
        console.log("ERROR add");
      } finally {
        setLoading(false);
      }
    }
  };

  const type = reportType ? "Uredi" : "Dodaj";

  return (
    <Modal
      title={type + " pregled"}
      visible={visibleModal}
      wrapClassName={styles.addOrEditReportTypeModal}
      onCancel={() => {
        onCloseModal();
      }}
      footer={[
        <Popover
          content={
            "Odustani od " +
            (reportType ? "uređivanja" : "dodavanja") +
            " pregleda"
          }
          trigger="hover"
        >
          <Button
            key="cancel"
            className={styles.cancelButton}
            onClick={() => {
              onCloseModal();
            }}
          >
            Odustani
          </Button>
        </Popover>,

        <Popover
          content={(reportType ? "Uredi" : "Sačuvaj") + " pregled"}
          trigger="hover"
        >
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
            className={styles.addButton}
          >
            Sačuvaj
          </Button>
        </Popover>,
      ]}
      width={600}
    >
      <div className={styles.modalFormWrapper}>
        <Form
          form={form}
          initialValues={initialValues}
          onFinish={onSubmit}
          onFinishFailed={({ errorFields }) => {
            form.scrollToField(errorFields[0].name);
          }}
        >
          <div className={styles.nameAndCheckboxFieldsWrapper}>
            <Row gutter={[16, 2]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Naziv"
                  labelCol={{ span: 24 }}
                  className={styles.labelForAddOrEditPatient}
                  
                  name="name"
                  rules={[
                    {
                      max:100,
                      message: 'Naziv pregleda mora sadržizi manje od 100 karaktera'
                    },
                    {
                      required: true,
                      message: "Unesite naziv!",
                    },
                  ]}
                >
                  <Input 
                    maxLength={100} 
                    autoComplete="off" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Cijena"
                  labelCol={{ span: 24 }}
                  className={styles.labelForAddOrEditPatient}
                  
                  name="price"
                  rules={[
                    {
                      required: true,
                      message: "Unesite cijenu!",
                    },
                  ]}
                >
                  <InputNumber
                    formatter={(value) =>
                      value
                        .toString()
                        .replace(",", ".")
                        .replace(/[^0-9.]/g, "")
                    }
                    parser={(input) =>
                      input.slice(
                        0,
                        input.indexOf(".") > 0
                          ? input.indexOf(".") + 5
                          : input.length
                      )
                    }
                    min="0"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Specijalizacija"
                  labelCol={{ span: 24 }}
                  className={styles.labelForAddOrEditPatient}
                  
                  name="specialization"
                  rules={[
                    {
                      required: true,
                      message: "Izaberite specijalizaciju!",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    style={{ width: 200 }}
                    optionFilterProp="data-searchvalue"
                    placeholder="Izaberite specijalizaciju"
                    notFoundContent={"Nema rezultata"}
                  >
                    {getSelectSpecializationOptions(specializations || [])}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </div>
    </Modal>
  );
};
